import { Card, Container } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useHreflang } from "../../i18n/hooks/useHreflang";
import { Link } from "react-router-dom";
import { useMemo } from "react";
import ReactCountryFlag from "react-country-flag";
import { getLocaleFromPath, getSlug, localePath } from "../../i18n/config";
import { metadataTranslations } from "../../i18n/metadata";
import "./osta.css";

type ProductCardData = {
    title: string;
    image: string;
    description: string;
    link: string;
    marker: { type: "flags" | "world"; flags: string[] };
};

const Osta = () => {
    const hreflang = useHreflang("osta");
    const locale = getLocaleFromPath("");
    const meta = metadataTranslations.osta[locale] || metadataTranslations.osta.fi;

    const products = useMemo<ProductCardData[]>(() => {
        const l = (catKey: string) => `${localePath("osta")}/${getSlug(catKey, locale)}`;
        return [
            {
                title: "SENIORITABLETTI",
                image: "/assets/icons/tablet.webp",
                description: "Saat kaiken valmiina palveluna. Maksat kuukausimaksun, johon sisältyy kaikki. Tabletti, liittymä, jalusta, Digihappy videopuhelut, optimoitu kotinäkymä, asetukset, it-tuki ja etähallinta. Tabletti on Digihappyn.",
                link: l("senioritabletti"),
                marker: { type: "flags", flags: ["FI", "SE", "GB", "IE"] as string[] },
            },
            {
                title: "SENIORIPUHELIN",
                image: "/assets/icons/mobile.png",
                description: "Saat kaiken valmiina palveluna. Maksat erikseen puhelimen ja kuukausimaksun. Omistat puhelimen. Kuukausimaksuun sisältyy Digihappy videopuhelut, it-tuki, etähallinta, muokattu kotinäkymä ja asetukset. Liittymä valintasi mukaan.",
                link: l("senioripuhelin"),
                marker: { type: "flags", flags: ["FI"] as string[] },
            },
            {
                title: "TURVARANNEKE",
                image: "/assets/icons/watch.png",
                description: "Saat kaiken valmiina palveluna. Maksat erikseen rannekkeen ja kuukausimaksun. Omistat rannekkeen. Kuukausimaksuun sisältyy hälytyspuhelut läheisille,liittymä paikannukseen ja puheluihin, it-tuki ja etähallinta.",
                link: l("turvaranneke"),
                marker: { type: "flags", flags: ["FI"] as string[] },
            },
            {
                title: "VIDEOPUHELU APPI",
                image: "/assets/icons/app_only_products.png",
                description: "Videopuhelut seniorin omalle tabletille tai puhelimelle. Kuukausimaksuun sisältyy apin käyttö seniorille ja enintään 6 läheiselle. Kokeile maksutta 14 päivää",
                link: l("videopuhelu_appi"),
                marker: { type: "world", flags: [] as string[] },
            },
            {
                title: "LISÄVARUSTEET",
                image: "/assets/icons/gadgets.png",
                description: "Jalusta, näytönsuoja, laturi, pitkä johto ja muuta yksittäisinä lisätuotteina, ei osana tabletti-, puhelin- tai turvarannekepalvelua.",
                link: l("lisatarvikkeet"),
                marker: { type: "flags", flags: ["FI"] as string[] },
            },
        ];
    }, [locale]);

    return (
        <>
            <Helmet>
                <title>{meta.title}</title>
                <meta name="description" content={meta.description} />
                <link rel="alternate" hrefLang="fi" href={hreflang.fi} />
                <link rel="alternate" hrefLang="en" href={hreflang.en} />
                <link rel="alternate" hrefLang="sv" href={hreflang.sv} />
                <link rel="alternate" hrefLang="x-default" href={hreflang.fi} />
            </Helmet>
            <Container fluid className="global-content" style={{ paddingTop: "2rem", paddingBottom: "0.5rem" }}>
                <h1 className="global-banner-title">Osta</h1>
                <p className="global-text global-text-normal" style={{ margin: "0.4rem 0 0 0" }}>Valitse haluamasi palvelu. Tilauksen voi tehdä läheinen tai seniori.</p>
            </Container>
            <Container className="global-content" fluid>
                <div className="osta-grid-container">
                    {products.map((product, key) => (
                        <div className="osta-grid-column" key={key}>
                            <ProductCard product={product} />
                        </div>
                    ))}
                </div>
            </Container>
        </>
    );
};

const ProductCard = ({ product }: { product: ProductCardData }) => {
    return (
        <Link to={product.link}>
            <Card className="osta-card-2024">
                <Card.Title className="image-text text-center osta-card-title">{product.title}</Card.Title>
                <div className="osta-image-container">
                    <img src={product.image} alt="T" className="osta-card-image" />
                </div>
                <Card.Text className="image-text text-center osta-card-text osta-card-description">
                    {product.description}
                    <span className="osta-availability-marker" aria-label="Availability">
                        {product.marker.type === "world" ? (
                            <span className="osta-availability-world" aria-label="Worldwide">🌍</span>
                        ) : (
                            product.marker.flags.map((flag) => (
                                <ReactCountryFlag key={`${product.title}-${flag}`} countryCode={flag} svg className="osta-availability-flag" aria-label={flag} />
                            ))
                        )}
                    </span>
                </Card.Text>
            </Card>
        </Link>
    );
};

export default Osta;
